import {
  CloseOutlined,
  DownOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Dropdown, Input, Menu, Spin, Tooltip } from 'antd';
import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';

import Table from '../../components/Table';
import { setQuery, setSelect, setUrl } from '../../store/reducers/dataSlice';
import { setSelectedMenu } from '../../store/reducers/uiSlice';
import * as roles from './../../services/constants/roles';

const checkValidUrl = (url) => {
  return url?.startsWith('http') ? url : `//${url}`;
};

const ALL_OPTION = { text: 'All', value: '__all__' };

const availableOptions = [
  ALL_OPTION,
  { text: 'Available', value: true },
  { text: 'Unavailable', value: false },
];

const getAvailableFromParam = (buyable) => {
  if (buyable === 'true') return { text: 'Available', value: true };
  if (buyable === 'false') return { text: 'Unavailable', value: false };
  return null;
};

const styles = {
  filterSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginTop: 20,
    marginBottom: 8,
    flexWrap: 'nowrap',
  },
  leftGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    flexWrap: 'nowrap',
    minWidth: 0,
    flex: 1,
  },
  search: {
    width: 280,
    minWidth: 200,
    flexShrink: 0,
  },
  filterChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    color: '#4d5e55',
    background: 'transparent',
    border: 'none',
    padding: '4px 0',
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
  },
  filterChipActive: {
    background: 'rgba(151, 190, 17, 0.22)',
    color: '#3d5a1a',
    borderRadius: 20,
    padding: '6px 14px',
    fontWeight: 600,
  },
  clearBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: 'none',
    background: 'transparent',
    color: '#eb7120',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    padding: '4px 0',
    whiteSpace: 'nowrap',
  },
  count: {
    color: '#4d5e55',
    fontSize: 14,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  chevron: {
    fontSize: 10,
  },
  dropdownPanel: {
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 3px 12px rgba(0, 0, 0, 0.12)',
    padding: 8,
    minWidth: 220,
  },
  dropdownSearch: {
    marginBottom: 8,
    borderColor: '#97BE11',
  },
  dropdownMenu: {
    maxHeight: 220,
    overflowY: 'auto',
    border: 'none',
    boxShadow: 'none',
  },
};

function ProductsPage(props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [count, setCount] = useState(null);
  const [productTypeFilter, setProductTypeFilter] = useState([]);
  const [vendorFilter, setVendorFilter] = useState([]);

  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(
    () => (searchParams.get('q') || '').trim(),
  );
  const [vendor, setVendor] = useState(null);
  const [type, setType] = useState(null);
  const [available, setAvailable] = useState(() =>
    getAvailableFromParam(searchParams.get('Buyable')),
  );
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const [resetPageToken, setResetPageToken] = useState(0);
  const [vendorMenuSearch, setVendorMenuSearch] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const skipPageReset = useRef(true);

  const isAdmin = props.roleId === roles.ADMIN_ROLE_ID;

  useEffect(() => {
    axios.get('/product-types?&_sort=Name:ASC').then((res) => {
      setProductTypeFilter(
        res.data.map((type) => {
          return { text: type.Name, value: type.id };
        }),
      );
    });
  }, []);

  useEffect(() => {
    axios.get('/vendors?&_sort=Name:ASC').then((res) => {
      setVendorFilter(
        res.data.map((type) => {
          return { text: type.Name, value: type.id };
        }),
      );
    });
  }, []);

  // Resolve vendor/type labels from URL once option lists are ready.
  useEffect(() => {
    if (filtersHydrated) return;

    const vendorId = searchParams.get('VendorId');
    const typeId = searchParams.get('productType');
    if (vendorId && vendorFilter.length === 0) return;
    if (typeId && productTypeFilter.length === 0) return;

    if (vendorId) {
      const match = vendorFilter.find(
        (option) => String(option.value) === String(vendorId),
      );
      if (match) setVendor(match);
    }
    if (typeId) {
      const match = productTypeFilter.find(
        (option) => String(option.value) === String(typeId),
      );
      if (match) setType(match);
    }
    setFiltersHydrated(true);
  }, [vendorFilter, productTypeFilter, searchParams, filtersHydrated]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    props.setSelectedMenu('40');
    props.setUrl('/products');
    props.setQuery('&_sort=id:DESC&');

    axios
      .get(
        `/products/count?&Deleted=0&${
          isAdmin ? '' : 'Buyable=true&'
        }`,
      )
      .then((res) => setCount(res.data))
      .catch((e) => console.log(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Before chip state is hydrated, apply filter ids from the URL so the
    // first products fetch is already filtered (Go Back must not load all).
    const vendorId = filtersHydrated
      ? vendor?.value ?? null
      : vendor?.value ?? searchParams.get('VendorId');
    const typeId = filtersHydrated
      ? type?.value ?? null
      : type?.value ?? searchParams.get('productType');
    const buyableFromUrl = searchParams.get('Buyable');
    const buyableValue = filtersHydrated
      ? available === null
        ? null
        : available.value
      : available !== null
        ? available.value
        : buyableFromUrl === 'true'
          ? true
          : buyableFromUrl === 'false'
            ? false
            : null;
    const searchTerm = filtersHydrated
      ? debouncedSearch
      : debouncedSearch || (searchParams.get('q') || '').trim();

    let select = `&Deleted=0&`;
    if (!isAdmin) {
      select += 'Buyable=true&';
    }
    if (vendorId !== null && vendorId !== undefined && vendorId !== '') {
      select += `VendorId=${encodeURIComponent(String(vendorId))}&`;
    }
    if (typeId !== null && typeId !== undefined && typeId !== '') {
      select += `productType=${encodeURIComponent(String(typeId))}&`;
    }
    if (isAdmin && buyableValue !== null && buyableValue !== undefined) {
      select += `Buyable=${encodeURIComponent(String(buyableValue))}&`;
    }
    if (searchTerm) {
      const term = encodeURIComponent(searchTerm);
      select += `_where[_or][0][Code_contains]=${term}&_where[_or][1][Name_contains]=${term}&`;
    }
    props.setSelect(select);

    if (skipPageReset.current) {
      skipPageReset.current = false;
    } else {
      setResetPageToken((token) => token + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vendor,
    type,
    available,
    debouncedSearch,
    isAdmin,
    filtersHydrated,
    searchParams,
  ]);

  // Keep list filters in the URL so Go Back from product detail restores them.
  useEffect(() => {
    if (!filtersHydrated) return;

    const next = new URLSearchParams();
    if (vendor) next.set('VendorId', String(vendor.value));
    if (type) next.set('productType', String(type.value));
    if (isAdmin && available !== null) {
      next.set('Buyable', String(available.value));
    }
    if (debouncedSearch) next.set('q', debouncedSearch);

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    filtersHydrated,
    vendor,
    type,
    available,
    debouncedSearch,
    isAdmin,
    searchParams,
    setSearchParams,
  ]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (search.trim()) n += 1;
    if (vendor) n += 1;
    if (type) n += 1;
    if (available !== null) n += 1;
    return n;
  }, [search, vendor, type, available]);

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setVendor(null);
    setType(null);
    setAvailable(null);
  };

  const renderFilterDropdown = (label, options, selected, onSelect, config = {}) => {
    const {
      searchable = false,
      searchPlaceholder = 'Search...',
      allLabel = 'All',
      menuSearch = '',
      onMenuSearchChange,
      open,
      onOpenChange,
    } = config;

    const baseOptions =
      options[0]?.value === ALL_OPTION.value
        ? options.filter((o) => o.value !== ALL_OPTION.value)
        : options;

    const filteredOptions = searchable
      ? baseOptions.filter((option) =>
          option.text.toLowerCase().includes(menuSearch.trim().toLowerCase()),
        )
      : baseOptions;

    const handleSelect = (key) => {
      if (key === String(ALL_OPTION.value)) {
        onSelect(null);
      } else {
        const option = baseOptions.find((o) => String(o.value) === key);
        if (option) onSelect(option);
      }
      if (onOpenChange) onOpenChange(false);
      if (onMenuSearchChange) onMenuSearchChange('');
    };

    const overlay = (
      <div
        style={styles.dropdownPanel}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {searchable && (
          <Input
            prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
            placeholder={searchPlaceholder}
            value={menuSearch}
            onChange={(e) => onMenuSearchChange?.(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            allowClear
            style={styles.dropdownSearch}
          />
        )}
        <Menu
          style={styles.dropdownMenu}
          selectedKeys={
            selected != null
              ? [String(selected.value)]
              : [String(ALL_OPTION.value)]
          }
          onClick={({ key }) => handleSelect(key)}
        >
          <Menu.Item key={String(ALL_OPTION.value)}>
            <strong>{allLabel}</strong>
          </Menu.Item>
          {filteredOptions.map((option) => (
            <Menu.Item key={String(option.value)}>{option.text}</Menu.Item>
          ))}
        </Menu>
      </div>
    );

    const dropdownProps = {
      overlay,
      trigger: ['click'],
    };

    if (searchable) {
      dropdownProps.visible = open;
      dropdownProps.onVisibleChange = (visible) => {
        onOpenChange?.(visible);
        if (!visible) onMenuSearchChange?.('');
      };
    }

    return (
      <Dropdown {...dropdownProps}>
        <button
          type="button"
          style={{
            ...styles.filterChip,
            ...(selected ? styles.filterChipActive : {}),
          }}
        >
          <span>{selected ? selected.text : label}</span>
          <DownOutlined style={styles.chevron} />
        </button>
      </Dropdown>
    );
  };

  const adminColumns = [
    {
      title: 'Commercial Cost',
      dataIndex: 'ReqularPrice',
      key: 'ReqularPrice',
      render: (text, rec) => (
        <div style={{ textAlign: 'right' }}>
          {text ? `$${text.toFixed(2)}` : 'N/A'}
        </div>
      ),
      sorter: {},
    },
    {
      title: 'PTV',
      dataIndex: 'PTV',
      key: 'PTV',
      render: (text) => (
        <div style={{ textAlign: 'right' }}>
          {text ? `$${text.toFixed(2)}` : 'N/A'}
        </div>
      ),
      sorter: {},
    },
    {
      title: 'Commodity Price',
      dataIndex: 'CommidityPrice',
      key: 'CommidityPrice',
      render: (text) => (
        <div style={{ textAlign: 'right' }}>
          {text ? `$${text.toFixed(2)}` : 'N/A'}
        </div>
      ),
      sorter: {},
    },
  ];

  const notAdminColumns = [
    {
      title: 'Commercial Cost',
      dataIndex: props.amCOOP ? 'CoOPReqularPrice' : 'ReqularPrice',
      key: 'ReqularPrice',
      render: (text, rec) =>
        text ? (
          <div style={{ textAlign: 'right' }}>{`$${text.toFixed(2)}`}</div>
        ) : (
          'N/A'
        ),
      sorter: {},
    },
    {
      title: 'PTV',
      dataIndex: 'PTV',
      key: 'PTV',
      render: (text) => (
        <div style={{ textAlign: 'right' }}>
          {text ? `$${text.toFixed(2)}` : 'N/A'}
        </div>
      ),
      sorter: {},
    },
    {
      title: 'Commodity Price',
      dataIndex: props.amCOOP ? 'CoOPCommidityPrice' : 'CommidityPrice',
      key: 'CommidityPrice',
      render: (text) =>
        text ? (
          <div style={{ textAlign: 'right' }}>{`$${text.toFixed(2)}`}</div>
        ) : (
          'N/A'
        ),
      sorter: {},
    },
  ];

  let columns = [
    {
      title: 'Code',
      dataIndex: 'Code',
      key: 'Code',
      render: (text, rec) =>
        text ? (
          <a
            target="_blank"
            href={`/products/product/${rec.id}`}
            style={{ color: 'black' }}
            rel="noreferrer"
          >
            {text}
          </a>
        ) : (
          'N/A'
        ),
      sorter: {},
    },
    {
      title: 'Name',
      dataIndex: 'Name',
      key: 'Name',
      render: (text) => (text ? text : 'N/A'),
      sorter: {},
    },
    {
      title: 'URL (Link to specs)',
      dataIndex: ['productAttachment', 'url'],
      key: 'specs',
      render: (text) =>
        text ? (
          <a
            className="btn"
            target="_blank"
            href={checkValidUrl(text)}
            style={{ padding: '3px 24px', background: '#97BE11' }}
            rel="noreferrer"
          >
            Spec
          </a>
        ) : (
          'N/A'
        ),
    },
    ...(isAdmin ? adminColumns : notAdminColumns),
    {
      title: 'Lead Time',
      dataIndex: 'TimeLead',
      key: 'TimeLead',
      render: (text) =>
        text ? <div style={{ textAlign: 'center' }}>{text} days</div> : 'N/A',
      sorter: {},
    },
    {
      title: 'Vendor',
      dataIndex: 'VendorId',
      key: 'VendorId',
      render: (text, rec) => {
        return text && text.Name ? text.Name : 'N/A';
      },
      sorter: {},
      location: 'VendorId.Name',
    },
    {
      title: 'Type',
      dataIndex: 'productType',
      key: 'productType',
      render: (text) => (text && text.Name ? text.Name : 'N/A'),
      location: 'productType.Name',
      sorter: {},
    },
    ...(isAdmin
      ? [
          {
            title: 'Available',
            dataIndex: 'Buyable',
            key: 'Buyable',
            render: (elem) =>
              elem ? (
                <div style={{ textAlign: 'center' }}>
                  <img
                    src="/vectors/green-check.png"
                    alt=""
                    style={{ width: '17px' }}
                  />
                </div>
              ) : (
                <div style={{ color: 'red', textAlign: 'center' }}>
                  <CloseOutlined />
                </div>
              ),
          },
        ]
      : []),
    {
      title: '',
      dataIndex: 'Buyable',
      key: 'actions',
      render: (text, rec) => (
        <Tooltip title="View" style={{ textAlign: 'center' }}>
          <Link to={`/products/product/${rec.id}`}>
            <img src="/vectors/eye.png" alt="" />
          </Link>
        </Tooltip>
      ),
      width: 50,
    },
  ];

  return (
    <div id="Products-page">
      <h2 className="title">All Products</h2>
      <Spin spinning={count === null}>
        {count ? (
          <>
            {isAdmin ? (
              <Link to={'/products/product/new'}>
                <div className="btn">Add Product</div>
              </Link>
            ) : (
              ''
            )}

            <div style={styles.filterSection}>
              <div style={styles.leftGroup}>
                <Input
                  prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
                  placeholder="Search by code or name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  allowClear
                  style={styles.search}
                />

                {renderFilterDropdown(
                  'Vendor',
                  vendorFilter,
                  vendor,
                  setVendor,
                  {
                    searchable: true,
                    searchPlaceholder: 'Search vendor...',
                    allLabel: 'All vendors',
                    menuSearch: vendorMenuSearch,
                    onMenuSearchChange: setVendorMenuSearch,
                    open: vendorDropdownOpen,
                    onOpenChange: setVendorDropdownOpen,
                  },
                )}
                {renderFilterDropdown('Type', productTypeFilter, type, setType)}
                {isAdmin &&
                  renderFilterDropdown(
                    'Available',
                    availableOptions,
                    available,
                    setAvailable,
                  )}

                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    style={styles.clearBtn}
                    onClick={clearFilters}
                  >
                    <CloseOutlined /> Clear ({activeFilterCount})
                  </button>
                )}
              </div>

              <div style={styles.count}>
                {props.filteredCount ?? 0}{' '}
                {(props.filteredCount ?? 0) === 1 ? 'product' : 'products'}
              </div>
            </div>

            <Table
              columns={columns}
              title="products"
              link="products"
              scroll={{ x: isAdmin ? 1500 : 1400 }}
              resetPageToken={resetPageToken}
            />
          </>
        ) : (
          <>
            <div className="empty-table">
              <div className="wrapper">
                <h3>— Your products is empty —</h3>
                {isAdmin && (
                  <Link to={`/products/product/new`} style={{ color: 'white' }}>
                    <div className="btn">Add products</div>
                  </Link>
                )}
              </div>
            </div>
          </>
        )}
      </Spin>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    roleId: state.me.roleId,
    amCOOP: state.me.data.IsCoOp,
    filteredCount: state.data.count,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    setUrl: (url) => dispatch(setUrl(url)),
    setSelect: (q) => dispatch(setSelect(q)),
    setQuery: (q) => dispatch(setQuery(q)),

    setSelectedMenu: (id) => dispatch(setSelectedMenu(id)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductsPage);
